/**
 * The Add user licence picker's rules (#592): which licences it offers, how a
 * search narrows them, and the one-line summary each licence carries.
 *
 * Pure (no React, no MUI, no fetch): the dialog renders what these return.
 */

import type { LicenceResponse } from '@shared/schemas';

import { countryLabel } from '@/features/licence/_shared/domain/licence-identity';

/**
 * The licences a new user may join: a licence deactivated from the Danger zone
 * (#582) refuses its users at sign-in, so it is not offered. The picker's "only
 * licence" auto-select counts these too, never the hidden ones.
 */
export function pickableLicences(licences: readonly LicenceResponse[]): LicenceResponse[] {
  return licences.filter((l) => l.status !== 'inactive');
}

/** The licences whose name contains the search, ignoring case and outer spaces. */
export function filterLicencesByName(
  licences: readonly LicenceResponse[],
  search: string,
): LicenceResponse[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...licences];
  return licences.filter((l) => l.name.toLowerCase().includes(needle));
}

/** "1 user", "3 users": the figure is the Licences index's Users column. */
export function userCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'user' : 'users'}`;
}

/**
 * "Club deal • France • 3 users". An older licence may have no category or no
 * country: that part is left out rather than shown as a dash.
 */
export function licenceDetailsLine(
  licence: Pick<LicenceResponse, 'client_category_label' | 'based_in' | 'user_count'>,
): string {
  return [
    licence.client_category_label,
    countryLabel(licence.based_in),
    userCountLabel(licence.user_count),
  ]
    .filter((part): part is string => Boolean(part))
    .join(' • ');
}

/**
 * "Active · 3 lists": the badge New List shows inside the picked licence's
 * field (#595): whether the licence is live, and how many lists it already has.
 */
export function licenceStatusLine(licence: Pick<LicenceResponse, 'status' | 'list_count'>): string {
  const status = licence.status === 'active' ? 'Active' : 'Inactive';
  return `${status} · ${licence.list_count} ${licence.list_count === 1 ? 'list' : 'lists'}`;
}
